import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle, Star, Phone, Users, Clock } from 'lucide-react';

const GoogleBusinessProfileService = () => {
  const { city } = useParams();
  const { currentLanguage } = useLanguage();
  const language = currentLanguage as SwissLanguage;
  
  const cityDisplayName = city ? getCityDisplayName(language, city) : null;
  const isHubPage = !city;
  
  const content = {
    de: {
      title: isHubPage 
        ? 'Google Business Profile Optimierung – Mehr Anrufe & Bewertungen'
        : `Google Unternehmensprofil ${cityDisplayName} – Mehr Anrufe & Bewertungen`,
      description: isHubPage
        ? 'Google Business Profile Optimierung für lokale Sichtbarkeit. Mehr Anrufe, bessere Bewertungen, höhere lokale Rankings in der Schweiz.'
        : `Google Business Profile Optimierung ${cityDisplayName}: Lokale Sichtbarkeit steigern, Rezensionen aufbauen, Posts & Angebote managen. Jetzt Setup & Optimierung sichern.`,
      h1: isHubPage
        ? 'Google Business Profile Optimierung'
        : `Google Business Profile ${cityDisplayName}`,
      subtitle: isHubPage
        ? 'Lokale Sichtbarkeit, die sich auszahlt'
        : `Mehr Anrufe aus ${cityDisplayName} – lokale Sichtbarkeit steigern`,
      hero: 'Ihr Google Business Profile ist Ihr wichtigster lokaler Verkaufskanal. Wir optimieren es für maximale Sichtbarkeit, mehr Anrufe und positive Bewertungen.',
      
      packages: [
        {
          name: 'GBP Setup',
          price: 'CHF 890',
          features: [
            'Vollständige Profil-Optimierung',
            'Kategorie-Optimierung',
            'Foto & Video Upload',
            'Rezensions-Setup',
            'NAP Konsistenz',
            'Einmalig'
          ]
        },
        {
          name: 'GBP Management',
          price: 'CHF 390/Monat',
          features: [
            'Monatliche Posts',
            'Rezensions-Management',
            'Q&A Betreuung',
            'Performance Tracking',
            'Konkurrenz-Monitoring',
            'Monatlicher Report'
          ],
          popular: true
        },
        {
          name: 'Local SEO Pro',
          price: 'CHF 790/Monat',
          features: [
            'Vollständiges GBP Management',
            'Lokale Citation Building',
            'Website Local SEO',
            'Rezensions-Kampagnen',
            'Local Pack Optimierung',
            'Multi-Location Support'
          ]
        }
      ],
      
      faq: [
        {
          question: `Warum ist Google Business Profile für ${cityDisplayName || 'lokale Unternehmen'} wichtig?`,
          answer: '80% der lokalen Suchen führen zu Anrufen oder Besuchen. Ein optimiertes GBP erhöht lokale Sichtbarkeit um 200% und generiert qualifizierte Leads.'
        },
        {
          question: 'Wie bekomme ich mehr positive Google Bewertungen?',
          answer: 'Systematische Nachfrage bei zufriedenen Kunden, Review-Links, automatisierte E-Mail-Sequenzen. Wir helfen dabei, mehr 5-Sterne Bewertungen zu generieren.'
        },
        {
          question: 'Was sind Google Posts und warum sind sie wichtig?',
          answer: 'Posts erscheinen direkt im Google Business Profile und erhöhen Engagement. Regelmässige Posts verbessern lokale Rankings und zeigen Aktivität.'
        },
        {
          question: 'Wie lange dauert GBP Optimierung?',
          answer: 'Setup und Grundoptimierung in 1-2 Wochen. Erste Ranking-Verbesserungen nach 4-6 Wochen. Vollständige lokale Dominanz nach 3-6 Monaten.'
        }
      ]
    }
  };

  const currentContent = content[language] || content.de;
  
  useEffect(() => {
    updateMetaTags({
      title: currentContent.title,
      description: currentContent.description,
      canonicalUrl: `https://www.future-level.ch/${language}/google-business-profile${city ? `/${city}` : ''}`,
      language: language,
      keywords: `Google Business Profile, Lokales SEO, ${cityDisplayName || 'Schweiz'}, Google Bewertungen`
    });

    const hreflangLinks = generateHreflangLinks({
      currentLanguage: language,
      currentPath: `/google-business-profile${city ? `/${city}` : ''}`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    const orgSchema = generateOrganizationSchema({ language });
    const serviceSchema = generateServiceSchema({
      serviceName: 'Google Business Profile Optimierung',
      serviceSlug: 'gbp',
      city,
      language,
      priceRange: 'CHF 390-790',
      description: currentContent.description,
      url: `https://www.future-level.ch/${language}/google-business-profile${city ? `/${city}` : ''}`
    });

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema]
    });
  }, [language, city, cityDisplayName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-orange-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-orange-600/20 text-orange-200 border-orange-500/30">
                <MapPin className="w-4 h-4 mr-2" />
                Local SEO Experte {cityDisplayName && `• ${cityDisplayName}`}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {currentContent.h1}
                <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent block">
                  {currentContent.subtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {currentContent.hero}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white">
                  <Star className="w-5 h-5 mr-2" />
                  GBP Audit anfordern
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {currentContent.packages.map((pkg, index) => (
                <Card key={index} className={`relative bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 ${pkg.popular ? 'ring-2 ring-orange-500' : ''}`}>
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-3xl font-bold text-orange-400">
                      {pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6 bg-orange-600 hover:bg-orange-700">
                      Jetzt starten
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default GoogleBusinessProfileService;